import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

/**
 * Passive page visit tracker. Fires a lightweight POST to /telemetry/page-visit
 * on every route change for authenticated users. Fire-and-forget — never blocks UI.
 */
export function usePageVisitTracker() {
  const location = useLocation();
  const { user } = useAuth();
  const lastPath = useRef<string>('');

  useEffect(() => {
    // Only track for logged-in users
    if (!user) return;

    const path = location.pathname;

    // Skip duplicate consecutive visits (e.g., re-renders on same page)
    if (path === lastPath.current) return;
    lastPath.current = path;

    // Skip auth/public routes
    const skipPrefixes = ['/login', '/register', '/change-password', '/get-credentials', '/privacy', '/dashboard/'];
    if (skipPrefixes.some((p) => path.startsWith(p))) return;

    // Fire and forget — don't await, don't catch
    apiClient('/telemetry/page-visit', {
      method: 'POST',
      body: { path },
    }).catch(() => {});
  }, [location.pathname, user]);
}
